import React from 'react'
import { InnerLink } from './collapsible-list-manager'
import { FormattedMessage } from 'react-intl'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'

const itemCountCss = css`
  color: ${theme.color.neutral50};
  font-size: 12px;
  padding-top: 5px;
  padding-right: 4px;
`

const toggleLinkContainerCss = css`
  display: flex;
  flex-direction: row;
  align-items: center;
`

interface CollapsiblePaginatedListManagerChildrenProps<T> {
  readonly visibleItems: T[]
  readonly isExpanded: boolean
  readonly onShowMore: () => void
  readonly onShowLess: () => void
  readonly showToggle: boolean
}

interface CollapsiblePaginatedListItemsProps<T> {
  /** The maximum number of items to show when the list is collapsed */
  readonly max: number
  /** The number of additional items to show each time "Show more" is clicked */
  readonly increment: number
  readonly children: (
    props: CollapsiblePaginatedListManagerChildrenProps<T>
  ) => JSX.Element
  readonly items: T[]
}

interface ToggleProps {
  readonly className?: string
  readonly totalItems: number
  readonly totalVisibleItems: number
  readonly onShowMore: () => void
  readonly onShowLess: () => void
}

interface ToggleLinkProps {
  readonly className?: string
  readonly totalItems: number
  readonly totalVisibleItems: number
  readonly onClick: () => void
  readonly labelId: string
}

export function CollapsiblePaginatedListToggle({
  className,
  totalItems,
  totalVisibleItems,
  onShowMore,
  onShowLess
}: ToggleProps) {
  const isFullyExpanded = totalItems === totalVisibleItems
  return (
    <ToggleLink
      className={className}
      totalItems={totalItems}
      totalVisibleItems={totalVisibleItems}
      onClick={isFullyExpanded ? onShowLess : onShowMore}
      labelId={isFullyExpanded ? 'action_showLess' : 'action_showMore'}
    />
  )
}

export function ShowLessLink({ onShowLess }) {
  return (
    <InnerLink onClick={onShowLess}>
      <FormattedMessage id="action_showLess" />
    </InnerLink>
  )
}

function ToggleLink({
  className,
  totalItems,
  totalVisibleItems,
  onClick,
  labelId
}: ToggleLinkProps) {
  return (
    <div className={cx(toggleLinkContainerCss, className)}>
      <div className={itemCountCss}>{`${totalVisibleItems}/${totalItems}`}</div>
      <InnerLink onClick={onClick}>
        <FormattedMessage id={labelId} />
      </InnerLink>
    </div>
  )
}

/** A collapsible list that expands incrementally */
export function CollapsiblePaginatedListManager<T>({
  max,
  increment,
  children,
  items
}: CollapsiblePaginatedListItemsProps<T>) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [lastVisibleItemIndex, setLastVisibleItemIndex] = React.useState(max)

  const visibleItems = items.slice(0, lastVisibleItemIndex)

  const determineNextIndex = () => {
    const x = lastVisibleItemIndex + increment
    return x <= items.length - 1 ? x : items.length
  }

  return children({
    visibleItems,
    isExpanded,
    onShowMore: () => {
      setIsExpanded(true)
      setLastVisibleItemIndex(determineNextIndex())
    },
    onShowLess: () => {
      setIsExpanded(false)
      setLastVisibleItemIndex(max)
    },
    showToggle: items.length > max
  })
}
